import { TranslateService } from '@ngx-translate/core';
import { ConfigInstanceToken } from '../../interfaces/config.interface';
import { ConfigConstants } from '../../config/config-constants';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { ValueHelperInstanceToken } from '../../interfaces/value-helper.interface';
import { ArrayModel } from './array.model';
import { CompositeModel } from './composite.model';
import { CrmCodeModel, ODataCrmCodeOutModel } from './crm-code.model';
import { JsonRadioModel, RadioModel } from './radio.model';
import { NotificationIdentifierModel } from '../low-level/notification-identifier.model';
import { RadioModelOptions } from '../options/radio-model-options';
import { TextModel } from '../primitive/text.model';
import { isDefined } from '../../utilities/is-defined';
import {
    ConfirmationComplaintsQcaFieldsModel,
    ConfirmationComplaintsQcaFieldsModelFromJson,
    customerLabel,
    healthcareInternalLabel,
    JsonConfirmationComplaintsQcaFieldsModel,
} from './confirmation-complaints-qca-fields.model';
import {
    ConfirmationComplaintsQcaCreationSetModel,
    ConfirmationComplaintsQcaCreationSetModelFromJson,
    JsonConfirmationComplaintsQcaCreationSetModel,
} from './confirmation-complaints-qca-creation-set.model';
import {
    ConfirmationComplaintsQcaTextsModel as ConfirmationComplaintsQcaTextsModel,
    ConfirmationComplaintsQcaTextsModelFromJson,
    JsonConfirmationComplaintsQcaTextsModel,
} from './confirmation-complaints-qca-texts.model';
import { NotificationDataInstanceToken } from '../../interfaces/notification-data.interface';
import { UserMasterInstanceToken } from '../../interfaces/user-master.interface';

function preventYes(radioModel: RadioModel<TextModel>): RadioModel<TextModel> {
    radioModel.options = new RadioModelOptions({ ...radioModel.options, invalidSelection: 0 });
    return radioModel;
}
function allowYes(radioModel: RadioModel<TextModel>): RadioModel<TextModel> {
    radioModel.options = new RadioModelOptions({ ...radioModel.options, invalidSelection: -1 });
    return radioModel;
}

export class ConfirmationComplaintsP58Model extends CompositeModel {
    public questionPSI1: RadioModel<TextModel>;
    public questionPSI2: RadioModel<TextModel>;
    public questionPSI3: RadioModel<TextModel>;
    public questionPPI4: RadioModel<TextModel>;
    public questionCYS5: RadioModel<TextModel>;
    private readonly codeModel: ArrayModel<CrmCodeModel>;

    public qcaCreationSet: ConfirmationComplaintsQcaCreationSetModel;
    public qcaFields: ConfirmationComplaintsQcaFieldsModel;
    public qcaTexts: ConfirmationComplaintsQcaTextsModel;

    private readonly hideComplaintsLinkForUserStatusType: boolean;
    public readonly showCyberSecurityComplaintQuestion: boolean;
    public readonly showInvivoQuestionnaire: boolean;
    public readonly showInvitroQuestionnaire: boolean;
    public complaintAlreadySubmitted: boolean;

    public ticketTitle = '';
    public q1descText = '';
    public q2descText = '';
    public q3descText = '';
    public q4descText = '';
    public q5descText = '';

    public processIndicator: ConfigConstants.PROCESS_INDICATOR;

    /**
     * Prepare a model for the Confirmation Complaints Model.
     *
     * @param notificationId notification where we are creating the complaint
     * @class
     */
    public constructor(public notificationId: NotificationIdentifierModel) {
        super('ConfirmationComplaintsP58Model', {});

        const injector = CustomInjector.getInjector();
        const translate = injector.get(TranslateService);
        const configService = injector.get(ConfigInstanceToken);
        const valueHelperService = injector.get(ValueHelperInstanceToken);
        const notificationService = injector.get(NotificationDataInstanceToken);
        const questions = valueHelperService.getComplaintQuestions();

        const notification = notificationService.getNotificationByIdentifier(notificationId);
        if (!isDefined(notification)) {
            throw new Error(
                `ConfirmationComplaintsP58Model: notification not found for id '${notificationId.toString()}'`,
            );
        }

        this.processIndicator = notification.processIndicator;

        this.showCyberSecurityComplaintQuestion = configService.getShowCyberSecurityQuestion();
        this.showInvivoQuestionnaire = this.processIndicator === ConfigConstants.PROCESS_INDICATOR.INVIVO;
        this.showInvitroQuestionnaire = this.processIndicator === ConfigConstants.PROCESS_INDICATOR.INVITRO;

        this.complaintAlreadySubmitted =
            notification.userStatus.includes(ConfigConstants.USER_STATUS.PSI) ||
            notification.userStatus.includes(ConfigConstants.USER_STATUS.PPI) ||
            notification.userStatus.includes(ConfigConstants.USER_STATUS.CYS);

        const q1 = questions.find(
            (valueHelper) => valueHelper.code === ConfigConstants.confirmationComplaintsQuestion1,
        );
        const q2 = questions.find(
            (valueHelper) => valueHelper.code === ConfigConstants.confirmationComplaintsQuestion2,
        );
        const q3 = questions.find(
            (valueHelper) => valueHelper.code === ConfigConstants.confirmationComplaintsQuestion3,
        );
        const q4 = questions.find(
            (valueHelper) => valueHelper.code === ConfigConstants.confirmationComplaintsQuestion4,
        );
        const q5 = questions.find(
            (valueHelper) => valueHelper.code === ConfigConstants.confirmationComplaintsQuestion5,
        );
        this.q1descText = q1?.descText ?? '';
        this.q2descText = q2?.descText ?? '';
        this.q3descText = q3?.descText ?? '';
        this.q4descText = q4?.descText ?? '';
        this.q5descText = q5?.descText ?? '';

        const yesOption = new DisplayDecorator(
            <string>translate.instant('CONFIRMATION_COMPLAINTS_OPTION_YES'),
            new TextModel(ConfigConstants.optionYes, ConfigConstants.optionYes),
        );
        const noOption = new DisplayDecorator(
            <string>translate.instant('CONFIRMATION_COMPLAINTS_OPTION_NO'),
            new TextModel(ConfigConstants.optionNo, ConfigConstants.optionNo),
        );

        const yesNoOptionGroup = [yesOption, noOption];

        this.questionPSI1 = new RadioModel<TextModel>(
            'PSI1',
            yesNoOptionGroup,
            new RadioModelOptions({ initialSelection: -1, invalidSelection: -1, required: true }),
        );

        this.questionPSI2 = new RadioModel<TextModel>(
            'PSI2',
            yesNoOptionGroup,
            new RadioModelOptions({ initialSelection: -1, invalidSelection: -1, required: true }),
        );

        this.questionPSI3 = new RadioModel<TextModel>(
            'PSI3',
            yesNoOptionGroup,
            new RadioModelOptions({ initialSelection: -1, invalidSelection: -1, required: true }),
        );

        this.questionPPI4 = new RadioModel<TextModel>(
            'PPI4',
            yesNoOptionGroup,
            new RadioModelOptions({ initialSelection: -1, invalidSelection: -1, required: true }),
        );

        this.questionCYS5 = new RadioModel<TextModel>(
            'CYS5',
            yesNoOptionGroup,
            new RadioModelOptions({ initialSelection: -1, invalidSelection: -1, required: true }),
        );

        // Create array model for outmodel
        const codes = [
            new CrmCodeModel({
                codeType: 'N',
                code: ConfigConstants.confirmationComplaintsQuestion1,
                codeGroup: '',
            }),
            new CrmCodeModel({
                codeType: 'N',
                code: ConfigConstants.confirmationComplaintsQuestion2,
                codeGroup: '',
            }),
            new CrmCodeModel({
                codeType: 'N',
                code: ConfigConstants.confirmationComplaintsQuestion3,
                codeGroup: '',
            }),
            new CrmCodeModel({
                codeType: 'N',
                code: ConfigConstants.confirmationComplaintsQuestion4,
                codeGroup: '',
            }),
            new CrmCodeModel({
                codeType: 'N',
                code: ConfigConstants.confirmationComplaintsQuestion5,
                codeGroup: '',
            }),
        ];

        this.codeModel = <ArrayModel<CrmCodeModel>>this.set(new ArrayModel<CrmCodeModel>('CrmCodesSet', codes));

        this.qcaCreationSet = new ConfirmationComplaintsQcaCreationSetModel(notification);
        this.qcaFields = new ConfirmationComplaintsQcaFieldsModel();
        this.qcaTexts = new ConfirmationComplaintsQcaTextsModel();
    }

    /**
     * Controls if the common complaints questions are displayed in the confirmation section.
     *
     * If any of the complaints questions are answered as "yes", then the section should be there.
     *
     * @returns boolean
     */
    public showCommonQuestionnaire(): boolean {
        return (
            !this.hideComplaintsLinkForUserStatusType &&
            (this.questionPSI1.selectedEntry?.value === ConfigConstants.optionYes ||
                this.questionPSI2.selectedEntry?.value === ConfigConstants.optionYes ||
                this.questionPSI3.selectedEntry?.value === ConfigConstants.optionYes ||
                this.questionPPI4.selectedEntry?.value === ConfigConstants.optionYes ||
                this.questionCYS5.selectedEntry?.value === ConfigConstants.optionYes)
        );
    }

    /**
     * Additional fields should only be visible if either PSI or CYS are selected; but not for PPI ("potential product
     * issue")
     *
     * @returns boolean
     */
    public showAdditionalQuestionnaire(): boolean {
        return (
            !this.hideComplaintsLinkForUserStatusType &&
            (this.questionPSI1.selectedEntry?.value === ConfigConstants.optionYes ||
                this.questionPSI2.selectedEntry?.value === ConfigConstants.optionYes ||
                this.questionPSI3.selectedEntry?.value === ConfigConstants.optionYes ||
                this.questionCYS5.selectedEntry?.value === ConfigConstants.optionYes)
        );
    }

    /**
     * Update invalid selections on all 5 leading question based on the following assumption:
     * - initial state is valid
     * - q2 cannot be combined with either q1 or q3
     * - q4 cannot be combined with either q1, q2, or q3
     *
     * Logic was defined by SHS and is document in https://evorait.atlassian.net/browse/SHP-8805
     *
     * Afterward, calculate priority of complaint by using the selected options, again following logic defined by SHS
     * in the ticket mentioned above.
     *
     */
    public updateAllowedQuestionsAndCalculatePriority(): void {
        const q1yes = this.questionPSI1.selectedEntry?.value === ConfigConstants.optionYes;
        const q2yes = this.questionPSI2.selectedEntry?.value === ConfigConstants.optionYes;
        const q3yes = this.questionPSI3.selectedEntry?.value === ConfigConstants.optionYes;
        const q4yes = this.questionPPI4.selectedEntry?.value === ConfigConstants.optionYes;
        const q5yes = this.questionCYS5.selectedEntry?.value === ConfigConstants.optionYes;

        // value 'true' means the state of a given question is valid
        const validityTracker = {
            q1: true,
            q2: true,
            q3: true,
            q4: true,
            q5: true,
        };

        // Check for invalid states
        if (q1yes && q2yes) {
            validityTracker.q1 = false;
            validityTracker.q2 = false;
        }
        if (q1yes && q4yes) {
            validityTracker.q1 = false;
            validityTracker.q4 = false;
        }
        if (q2yes && q4yes) {
            validityTracker.q2 = false;
            validityTracker.q4 = false;
        }
        if (q3yes && q4yes) {
            validityTracker.q3 = false;
            validityTracker.q4 = false;
        }

        // update radio models
        this.questionPSI1 = validityTracker.q1 ? allowYes(this.questionPSI1) : preventYes(this.questionPSI1);
        this.questionPSI2 = validityTracker.q2 ? allowYes(this.questionPSI2) : preventYes(this.questionPSI2);
        this.questionPSI3 = validityTracker.q3 ? allowYes(this.questionPSI3) : preventYes(this.questionPSI3);
        this.questionPPI4 = validityTracker.q4 ? allowYes(this.questionPPI4) : preventYes(this.questionPPI4);
        this.questionCYS5 = validityTracker.q5 ? allowYes(this.questionCYS5) : preventYes(this.questionCYS5);

        // update priority based on matrix given by SHS in https://evorait.atlassian.net/browse/SHP-8805
        let prio = 0;
        if (q1yes || q2yes || q3yes) {
            prio = 1;
        }
        if (q4yes) {
            prio = 2;
        }
        if (q5yes) {
            prio += 4;
        }

        this.qcaCreationSet.priority.value = prio.toString();

        // update ticket title based on selection questions
        const invitroLabel = 'DX ';
        const safetyLabel = 'Safety';
        const productLabel = 'Product';
        const cyberSecurityLabel = 'Cyber Security';
        const prefix = this.processIndicator === ConfigConstants.PROCESS_INDICATOR.INVITRO ? invitroLabel : '';
        const firstIssue =
            q1yes || q2yes || q3yes ? safetyLabel : q4yes ? productLabel : q5yes ? cyberSecurityLabel : '';
        const secondIssue = firstIssue !== cyberSecurityLabel && q5yes ? ` & ${cyberSecurityLabel}` : '';
        this.ticketTitle = `${prefix}Potential ${firstIssue}${secondIssue} Issue`;
    }

    public getOutModel(): ODataConfirmationComplaintsOutModel {
        // update values in CrmCodeModels
        const q01 = this.codeModel.value.find(
            (codeModel) => codeModel.code.value === ConfigConstants.confirmationComplaintsQuestion1,
        );

        const q02 = this.codeModel.value.find(
            (codeModel) => codeModel.code.value === ConfigConstants.confirmationComplaintsQuestion2,
        );

        const q03 = this.codeModel.value.find(
            (codeModel) => codeModel.code.value === ConfigConstants.confirmationComplaintsQuestion3,
        );

        const q04 = this.codeModel.value.find(
            (codeModel) => codeModel.code.value === ConfigConstants.confirmationComplaintsQuestion4,
        );

        const q05 = this.codeModel.value.find(
            (codeModel) => codeModel.code.value === ConfigConstants.confirmationComplaintsQuestion5,
        );

        if (!q01 || !q02 || !q03 || !q04 || !q05) {
            throw new Error(`Could not find all required complaint questions!`);
        }

        q01.codeText.value = this.questionPSI1.selectedEntry.value;
        q02.codeText.value = this.questionPSI2.selectedEntry.value;
        q03.codeText.value = this.questionPSI3.selectedEntry.value;
        q04.codeText.value = this.questionPPI4.selectedEntry.value;

        // fallback value needed since Q05 can be hidden in the frontend
        q05.codeText.value = this.questionCYS5.selectedEntry?.value ?? '';

        const outModel = <ODataConfirmationComplaintsOutModel>super.getOutModel();

        outModel.QcaCreationSet = this.qcaCreationSet.getOutModel();
        outModel.QcaTextsSet = this.qcaTexts.getOutModel();
        outModel.QcaFieldSet = this.qcaFields.getOutModel();

        // SHP-9379: as a requirement the NotificationText of the newly generated MQ notification needs to be a short
        // version (max 40 char) of the QcaTextSet's Problem value. Because the problem property from qcaTexts is influencing the
        // outModel of qcaCreationSet, we need to solve it here on the parent model.
        outModel.QcaCreationSet = {
            ...outModel.QcaCreationSet,
            NotificationText: this.qcaTexts.problem.value?.substring(0, 40) ?? '',
        };

        return outModel;
    }

    public updateInitiatedBy(): void {
        const injector = CustomInjector.getInjector();
        const selectedValue = this.qcaFields.initiatedBy.getSelectedValue();
        if (selectedValue === customerLabel) {
            const notificationService = injector.get(NotificationDataInstanceToken);
            const notification = notificationService.getNotificationByIdentifier(this.notificationId);
            if (!isDefined(notification)) {
                throw new Error(
                    `ConfirmationComplaintsP58Model: updateInitiatedBy: notification ${this.notificationId.toString()} does not exist!`,
                );
            }

            this.qcaCreationSet.updateInitiator(
                notification?.getContactName() ?? '',
                notification?.address.email.value ?? '',
                notification?.getContactPhone() ?? '',
            );
        } else if (selectedValue === healthcareInternalLabel) {
            const userMasterService = injector.get(UserMasterInstanceToken);

            const email = userMasterService.getSelectedUserModel().getEmailId();
            // user's name and phone number are not available in PACE due to privacy concerns
            this.qcaCreationSet.updateInitiator('', email, '');
        }
    }
}

export class ConfirmationComplaintsModelFromJson extends ConfirmationComplaintsP58Model {
    public constructor(data: JsonConfirmationComplaintsModel, notificationId: NotificationIdentifierModel) {
        super(notificationId);

        const injector = CustomInjector.getInjector();
        const notificationService = injector.get(NotificationDataInstanceToken);
        const notification = notificationService.getNotificationByIdentifier(notificationId);
        if (!isDefined(notification)) {
            throw new Error(
                `ConfirmationComplaintsP58Model: notification not found for id '${notificationId.toString()}'`,
            );
        }

        this.questionPSI1.selectByPredicate((entry) => entry.value === data.questionPSI1?.selectedEntry?.model.value);
        this.questionPSI2.selectByPredicate((entry) => entry.value === data.questionPSI2?.selectedEntry?.model.value);
        this.questionPSI3.selectByPredicate((entry) => entry.value === data.questionPSI3?.selectedEntry?.model.value);
        this.questionPPI4.selectByPredicate((entry) => entry.value === data.questionPPI4?.selectedEntry?.model.value);
        this.questionCYS5.selectByPredicate((entry) => entry.value === data.questionCYS5?.selectedEntry?.model.value);

        this.ticketTitle = data.ticketTitle ?? '';
        this.qcaCreationSet = data.qcaCreationSet
            ? new ConfirmationComplaintsQcaCreationSetModelFromJson(data.qcaCreationSet, notification)
            : new ConfirmationComplaintsQcaCreationSetModel(notification);
        this.qcaFields = data.qcaFields
            ? new ConfirmationComplaintsQcaFieldsModelFromJson(data.qcaFields)
            : new ConfirmationComplaintsQcaFieldsModel();
        this.qcaTexts = data.qcaTexts
            ? new ConfirmationComplaintsQcaTextsModelFromJson(data.qcaTexts)
            : new ConfirmationComplaintsQcaTextsModel();
    }
}

export interface JsonConfirmationComplaintsModel {
    questionPSI1?: JsonRadioModel;
    questionPSI2?: JsonRadioModel;
    questionPSI3?: JsonRadioModel;
    questionPPI4?: JsonRadioModel;
    questionCYS5?: JsonRadioModel;
    processIndicator?: ConfigConstants.PROCESS_INDICATOR;
    qcaCreationSet?: JsonConfirmationComplaintsQcaCreationSetModel;
    qcaFields?: JsonConfirmationComplaintsQcaFieldsModel;
    qcaTexts?: JsonConfirmationComplaintsQcaTextsModel;
    ticketTitle?: string;
}

export interface ODataConfirmationComplaintsOutModel extends ODataObject {
    CrmCodesSet: ODataCrmCodeOutModel[];
}
